import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkle, FlaskConical } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { isTrustedImageUrl } from "@/lib/imageTrust";
import { ProductPlaceholder } from "@/components/ProductPlaceholder";
import { ReviewsBlock } from "@/components/ReviewsBlock";

export default function StrainDetail() {
  const { slug } = useParams();
  const { location } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = location ? { lat: location.lat, lon: location.lon } : {};
    api
      .get(`/strains/${slug}`, { params })
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(e?.response?.status === 404 ? "not_found" : "error");
      })
      .finally(() => setLoading(false));
  }, [slug, location]);

  if (loading) {
    return (
      <main className="container-narrow py-10">
        <Skeleton className="h-72 rounded-2xl" />
      </main>
    );
  }
  if (error || !data) {
    return (
      <main className="container-narrow py-16" data-testid="strain-detail-empty">
        <Link
          to="/strains"
          className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
        >
          ← All strains
        </Link>
        <div className="mt-8 border border-dashed border-border rounded-2xl p-16 text-center">
          <div className="font-serif text-3xl mb-3">
            We couldn&apos;t find that strain on Bud Link.
          </div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            {error === "not_found"
              ? "No dispensary in our index is currently carrying this strain. Try searching from the home page or browse the full strain directory."
              : "Something went wrong loading this page. Refresh in a moment, or jump back to search."}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link
              to="/search"
              data-testid="strain-detail-search-cta"
              className="h-10 px-5 inline-flex items-center rounded-full bg-foreground text-background text-xs hover:bg-foreground/90"
            >
              Search strains
            </Link>
            <Link
              to="/strains"
              data-testid="strain-detail-list-cta"
              className="h-10 px-5 inline-flex items-center rounded-full border border-border text-xs hover:bg-card"
            >
              Browse strain directory
            </Link>
          </div>
        </div>
      </main>
    );
  }
  const { strain, listings, lowest_price, listing_count } = data;

  return (
    <main className="container-narrow py-10">
      <Link
        to="/strains"
        data-testid="strain-detail-back"
        className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground"
      >
        ← All strains
      </Link>

      <div className="grid md:grid-cols-[1.1fr_1fr] gap-10 mt-6 items-start">
        <div className="aspect-square rounded-3xl overflow-hidden bg-white border border-border relative">
          {strain.image_url ? (
            <img
              src={strain.image_url}
              alt={strain.name}
              data-testid="strain-detail-image"
              className="w-full h-full object-contain"
              decoding="async"
              onError={(e) => {
                // Same iOS-PWA-friendly retry pattern as ProductCard: a
                // single cache-busted reload before falling back to the
                // SVG placeholder so a cellular blip doesn't strand the
                // hero image behind a broken-image icon.
                const img = e.currentTarget;
                const tries = parseInt(img.dataset.retry || "0", 10);
                if (tries < 1) {
                  img.dataset.retry = String(tries + 1);
                  const sep = img.src.includes("?") ? "&" : "?";
                  img.src = `${strain.image_url}${sep}r=${Date.now()}`;
                  return;
                }
                const wrap = img.parentElement;
                if (wrap) {
                  img.style.display = "none";
                  const fb = wrap.querySelector("[data-strain-fallback]");
                  if (fb) fb.style.display = "block";
                }
              }}
            />
          ) : null}
          <div
            data-strain-fallback
            style={{
              display: strain.image_url ? "none" : "block",
              position: "absolute",
              inset: 0,
            }}
          >
            <ProductPlaceholder
              strainName={strain.name}
              brand=""
              category="flower"
              testId={`strain-placeholder-${strain.slug}`}
            />
          </div>
        </div>
        <div>
          <div className="eyebrow mb-3 flex items-center gap-2">
            <Sparkle className="w-3.5 h-3.5" /> {strain.type}
          </div>
          <h1 className="font-serif text-5xl tracking-tight">{strain.name}</h1>
          <p className="text-foreground/70 mt-4 leading-relaxed">
            {strain.description}
          </p>

          <div className="grid grid-cols-2 gap-3 mt-7">
            <div className="border border-border rounded-2xl p-5">
              <div className="eyebrow mb-1">Lowest nearby</div>
              <div className="font-serif text-3xl">
                {lowest_price != null ? `$${lowest_price.toFixed(2)}` : "—"}
              </div>
            </div>
            <div className="border border-border rounded-2xl p-5">
              <div className="eyebrow mb-1">Listings</div>
              <div className="font-serif text-3xl">{listing_count}</div>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="eyebrow mb-2">Effects</div>
              <div className="flex flex-wrap gap-2">
                {strain.effects?.map((e) => (
                  <span key={e} className="pill capitalize">{e}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="eyebrow mb-2">Flavor profile</div>
              <div className="flex flex-wrap gap-2">
                {strain.flavors?.map((f) => (
                  <span key={f} className="pill capitalize">{f}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Typical terpene profile. Curated numbers for popular strains,
              a strain-type default otherwise — clearly flagged so consumers
              know they're looking at typical values, not batch lab results. */}
          {strain.terpenes && (
            <div
              data-testid="strain-terpenes"
              className="mt-7 border border-border rounded-2xl p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-emerald-300" strokeWidth={1.8} />
                  <div className="eyebrow">Typical terpene profile</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-foreground/50">
                  Dominant · <span className="text-emerald-300">{strain.terpenes.dominant}</span>
                </span>
              </div>
              <div className="space-y-3">
                {strain.terpenes.profile.map((t) => (
                  <div key={t.name} data-testid={`terpene-${t.name.toLowerCase()}`}>
                    <div className="flex items-baseline justify-between text-sm mb-1">
                      <span className="font-medium text-foreground/90">{t.name}</span>
                      <span className="text-xs text-foreground/60 tabular-nums">
                        {Math.round(t.share * 100)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-background rounded-full overflow-hidden border border-border/60">
                      <div
                        className="h-full bg-emerald-500/70 rounded-full"
                        style={{ width: `${Math.round(t.share * 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-foreground/60 mt-1.5 leading-relaxed">
                      <span className="text-foreground/50">Aroma:</span> {t.aroma}
                      <span className="mx-1.5 text-foreground/30">·</span>
                      <span className="text-foreground/50">Effects:</span> {t.effects}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-foreground/50 mt-4 leading-relaxed">
                {strain.terpenes.source === "curated"
                  ? "Typical profile based on public strain data. Individual batches vary — check the dispensary's lab report for the exact terpene percentages on the jar you're buying."
                  : "Generic profile based on strain type — no curated data for this strain yet. Ask your dispensary for the batch-specific lab report."}
              </p>
            </div>
          )}
        </div>
      </div>

      <section className="mt-16">
        <h2 className="font-serif text-3xl mb-1">All listings</h2>
        <p className="text-sm text-muted-foreground mb-6">
          {location
            ? `Sorted by distance from ${location.city || "you"}`
            : "Set a location to sort by distance"}
        </p>
        {listings.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-16 text-center text-muted-foreground">
            No listings yet for this strain.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      {/* Native strain reviews — flavor, effects, potency perceptions */}
      {strain?.slug && (
        <section className="container-narrow mt-6 mb-12">
          <ReviewsBlock kind="strains" slug={strain.slug} />
        </section>
      )}
    </main>
  );
}
